/* eslint-disable no-return-assign */
/* eslint-disable no-underscore-dangle */
import { RefObject, useCallback, useLayoutEffect, useState } from 'react'
import useLatest from '@react-hook/latest'

/**
 * A React hook that fires a callback whenever ResizeObserver detects a change to its size
 * code extracted from https://github.com/jaredLunde/react-hook/blob/master/packages/resize-observer/src/index.tsx in order to not include the polyfill for resize-observer
 *
 * @internal
 */
export function useResizeObserver<T extends HTMLElement>(
  target: RefObject<T>,
  callback: UseResizeObserverCallback,
  isPiP?: boolean,
) {
  const resizeObserver = getResizeObserver(isPiP)
  const storedCallback = useLatest(callback)

  useLayoutEffect(() => {
    let didUnsubscribe = false

    const targetEl = target.current
    if (!targetEl) return

    function cb(entry: ResizeObserverEntry, observer: ResizeObserver) {
      if (didUnsubscribe) return
      storedCallback.current(entry, observer)
    }

    if (isPiP) {
      resizeObserver?.unsubscribe(targetEl as HTMLElement, cb)
    } else {
      resizeObserver?.subscribe(targetEl as HTMLElement, cb)
    }

    return () => {
      didUnsubscribe = true
      resizeObserver?.unsubscribe(targetEl as HTMLElement, cb)
    }
  }, [target, resizeObserver, storedCallback, isPiP])

  return resizeObserver?.observer
}

function createResizeObserver(isPiP?: boolean) {
  let ticking = false
  let allEntries: ResizeObserverEntry[] = []

  const callbacks: Map<unknown, Array<UseResizeObserverCallback>> = new Map()

  if (typeof window === 'undefined') {
    return
  }

  const observer = isPiP
    ? null
    : new ResizeObserver((entries: ResizeObserverEntry[], obs: ResizeObserver) => {
        allEntries = allEntries.concat(entries)

        if (!ticking) {
          window?.requestAnimationFrame(() => {
            const triggered = new Set<Element>()

            for (let i = 0; i < allEntries.length; i++) {
              if (triggered.has(allEntries[i].target)) continue
              triggered.add(allEntries[i].target)
              const cbs = callbacks.get(allEntries[i].target)
              cbs?.forEach((cb) => cb(allEntries[i], obs))
            }

            allEntries = []
            ticking = false
          })
        }

        ticking = true
      })

  return {
    observer,
    subscribe(target: HTMLElement, callback: UseResizeObserverCallback) {
      observer?.observe(target)
      const cbs = callbacks.get(target) ?? []
      cbs.push(callback)
      callbacks.set(target, cbs)
    },
    unsubscribe(target: HTMLElement, callback: UseResizeObserverCallback) {
      const cbs = callbacks.get(target) ?? []
      if (cbs.length === 1) {
        observer?.unobserve(target)
        callbacks.delete(target)
        return
      }
      const cbIndex = cbs.indexOf(callback)
      if (cbIndex !== -1) cbs.splice(cbIndex, 1)
      callbacks.set(target, cbs)
    },
  }
}

let _resizeObserver: ReturnType<typeof createResizeObserver> = null

const getResizeObserver = (isPiP?: boolean) =>
  !_resizeObserver ? (_resizeObserver = createResizeObserver(isPiP)) : _resizeObserver

export type UseResizeObserverCallback = (
  entry: ResizeObserverEntry,
  observer: ResizeObserver,
) => unknown

export interface ISize {
  width: number
  height: number
}

export const useSize = (target: RefObject<HTMLDivElement>, isPiP?: boolean) => {
  const [size, setSize] = useState<ISize>(() => {
    if (target.current) {
      const rect = target.current.getBoundingClientRect()
      return { width: rect.width, height: rect.height }
    }
    return { width: 0, height: 0 }
  })

  const resizeCallback = useCallback((entry: ResizeObserverEntry) => {
    setSize({
      width: entry.contentRect.width,
      height: entry.contentRect.height,
    })
  }, [])

  useResizeObserver(target, resizeCallback, isPiP)

  return size
}
